import React, { useState } from 'react'
import { supabase, supabaseBackgroundAuth } from '../lib/supabase'
import { getSignupEmailRedirectUrl } from '../lib/authRedirects'

const CreateAdminModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      setLoading(false)
      return
    }

    try {
      // Ensure current session is authenticated (admin-only route should already enforce this)
      const { data: currentUser } = await supabase.auth.getUser()
      if (!currentUser?.user) {
        throw new Error('Admin authentication required')
      }

      // Check if a user with this email already exists
      const { data: existingUser, error: existingUserError } = await supabase
        .from('users')
        .select('id')
        .eq('email', formData.email)
        .maybeSingle()

      if (existingUserError) {
        console.error('Error checking existing admin user:', existingUserError)
      }

      if (existingUser) {
        setError('A user with this email address already exists')
        setLoading(false)
        return
      }

      // Create the admin auth user using the background client so the
      // current session is not affected.
      const signupEmailRedirectTo = getSignupEmailRedirectUrl()
      const { data: authData, error: authError } = await supabaseBackgroundAuth.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: signupEmailRedirectTo,
          data: {
            role: 'admin',
          },
        },
      })

      if (authError) {
        throw authError
      }

      if (!authData?.user) {
        throw new Error('Failed to create admin account')
      }

      // Insert admin into public.users table. If a database trigger already
      // created a corresponding row for this Auth user, this insert may raise
      // a unique-violation error (code 23505). We treat that specific case as
      // benign and continue.
      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: formData.email,
          role: 'admin'
        })

      if (userError && userError.code !== '23505') {
        throw userError
      }

      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: ''
      })

      if (onSuccess) onSuccess()
      onClose()
    } catch (err) {
      console.error('Create admin error:', err)

      if (err.code === '23505') {
        setError('A user with this email address already exists')
      } else if (err.message?.includes('User already registered')) {
        setError('A user with this email address already exists')
      } else if (err.message?.includes('Invalid email')) {
        setError('Please enter a valid email address')
      } else if (err.message?.includes('Password')) {
        setError('Password must be at least 6 characters long')
      } else if (err.message?.includes('Admin authentication required')) {
        setError('You must be logged in as an admin to create admins')
      } else {
        setError(err.message || 'Failed to create admin account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Create New Admin</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Admin Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Admin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateAdminModal
